import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Users } from "lucide-react";
import { CelebrityCard } from "../components/CelebrityCard";
import { VersusBadge } from "../components/VersusBadge";
import { RoundProgress, StageProgress } from "../components/RoundProgress";
import { ReasonPicker, DecidingFactorPicker, DifficultyPicker } from "../components/Pickers";
import { RoundTimer, DangerFlash } from "../components/RoundTimer";
import { getCustomGame, saveCustomGame } from "../lib/storage";
import { findCeleb } from "../data/celebrities";
import { computeCustomComparison } from "../lib/scoring";
import { getMashFactor, DEFAULT_MASH_FACTOR_ID } from "../data/mashFactors";
import { playClick, playSelect, playAffirm, playError } from "../lib/sounds";
import { StartButton } from "../components/StartButton";

const STAGES = ["Choose", "Explain", "Predict"];

export default function PlayInvitation() {
  const nav = useNavigate();
  const { code } = useParams();
  const [game, setGame] = useState(null);
  const [started, setStarted] = useState(false);
  const [roundIdx, setRoundIdx] = useState(0);
  const [stageIdx, setStageIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [danger, setDanger] = useState(false);

  const mashFactor = useMemo(
    () => getMashFactor(game?.mashFactorId || DEFAULT_MASH_FACTOR_ID),
    [game]
  );

  useEffect(() => {
    const g = getCustomGame(code);
    setGame(g);
    if (g) {
      setAnswers(
        g.rounds.map((r) => ({
          roundId: r.id,
          selectedCelebrityId: null,
          reasons: [],
          decidingFactor: null,
          difficulty: null,
          majorityPredictionId: null,
        }))
      );
    }
  }, [code]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [roundIdx, stageIdx]);

  const round = game?.rounds[roundIdx];
  const a = round && findCeleb(round.aId);
  const b = round && findCeleb(round.bId);
  const roundBroken = round && (!a || !b);
  const ans = answers[roundIdx];

  const setAns = (patch) => {
    setAnswers((prev) => prev.map((x, i) => (i === roundIdx ? { ...x, ...patch } : x)));
  };

  const canContinue = useMemo(() => {
    if (!ans) return false;
    if (stageIdx === 0) return !!ans.selectedCelebrityId;
    if (stageIdx === 1) return ans.reasons.length >= 1 && !!ans.decidingFactor && !!ans.difficulty;
    if (stageIdx === 2) return !!ans.majorityPredictionId;
    return false;
  }, [stageIdx, ans]);

  if (!game) {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-16 pb-nav text-center">
          <h1 className="font-display text-4xl tracking-tight">Invitation not found</h1>
          <p className="mt-2 text-sm text-[#FDFBF7]/70">This custom smash isn&rsquo;t in local storage.</p>
          <button data-testid="invite-home-btn" onClick={() => nav("/home")} className="mt-6 rounded-full bg-[#FF107A] px-6 py-3 font-bold glow-pink">
            Home
          </button>
        </div>
      </div>
    );
  }

  const finish = () => {
    const comparison = computeCustomComparison(game, answers);
    saveCustomGame({ ...game, participantAnswers: answers, comparison, status: "completed" });
    nav(`/compare/${code}`);
  };

  const handleContinue = () => {
    if (!canContinue) { playError(); return; }
    playAffirm();
    if (stageIdx < 2) return setStageIdx(stageIdx + 1);
    if (roundIdx < game.rounds.length - 1) {
      setRoundIdx(roundIdx + 1);
      setStageIdx(0);
    } else finish();
  };

  const handleBack = () => {
    playClick();
    if (stageIdx > 0) setStageIdx(stageIdx - 1);
    else if (roundIdx > 0) {
      setRoundIdx(roundIdx - 1);
      setStageIdx(2);
    } else setStarted(false);
  };

  if (!started) {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-6 pb-nav">
          <button data-testid="invite-back-btn" onClick={() => nav("/home")} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
            <ArrowLeft size={16} /> Home
          </button>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 rounded-3xl border border-[#FF107A]/30 bg-[#FF107A]/5 p-6"
          >
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#FF107A] font-semibold">You&rsquo;ve been invited</div>
            <h1 className="font-display text-4xl leading-tight tracking-tight mt-2">
              {game.organizerName} created a Celebrity Smash <span className="text-[#00E5FF]">especially for you.</span>
            </h1>
            {game.invitationMessage && (
              <p className="mt-4 text-[#FDFBF7]/85 text-sm leading-relaxed italic">&ldquo;{game.invitationMessage}&rdquo;</p>
            )}
            <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-xl bg-black/30 border border-white/10 p-3">
                <div className="tracking-[0.2em] uppercase text-[#FDFBF7]/50">Custom rounds</div>
                <div className="font-display text-3xl mt-1">{game.rounds.length}</div>
              </div>
              <div className="rounded-xl bg-black/30 border border-white/10 p-3">
                <div className="tracking-[0.2em] uppercase text-[#FDFBF7]/50 flex items-center gap-1"><Clock size={12} /> Time</div>
                <div className="font-display text-3xl mt-1">~5 min</div>
              </div>
            </div>
            <StartButton
              onClick={() => setStarted(true)}
              testId="start-invite-btn"
              label="Start Smash"
            />
            <p className="mt-3 text-[11px] text-[#FDFBF7]/50 text-center">
              Organizer&rsquo;s predictions & notes are hidden until you finish all 5 rounds.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-16 pb-nav">
        <button data-testid="invite-back-btn" onClick={handleBack} className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="mt-4">
          <RoundProgress current={roundIdx + 1} total={game.rounds.length} stage={STAGES[stageIdx]} />
        </div>
        <div className="mt-3">
          <StageProgress stage={STAGES[stageIdx]} />
        </div>
        <div className="mt-4">
          <RoundTimer resetKey={`${roundIdx}-${stageIdx}`} onDangerChange={setDanger} />
        </div>
        <DangerFlash active={danger} />

        {roundBroken && (
          <div
            data-testid="iv-broken-round"
            className="mt-6 rounded-2xl border border-[#FF107A]/50 bg-[#FF107A]/10 px-4 py-4 text-sm text-[#FDFBF7]/90"
          >
            This round's celebrities are missing from the current library. Skip to keep going.
            <button
              data-testid="iv-skip-round-btn"
              onClick={() => {
                playClick();
                if (roundIdx < game.rounds.length - 1) { setRoundIdx(roundIdx + 1); setStageIdx(0); }
                else finish();
              }}
              className="mt-3 rounded-full bg-[#FF107A] text-[#FDFBF7] px-4 py-2 font-bold text-xs tracking-wide"
            >
              Skip round
            </button>
          </div>
        )}

        {!roundBroken && (
        <AnimatePresence mode="wait">
          <motion.div
            key={`${roundIdx}-${stageIdx}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="mt-6"
          >
            {stageIdx === 0 && (
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#FFD700] font-bold">Smash Factor</div>
                <h2
                  data-testid="iv-mash-factor-heading"
                  className="font-display text-3xl tracking-tight leading-tight mt-1"
                >
                  {mashFactor.label}
                </h2>
                <div className="relative mt-6 grid grid-cols-2 gap-3">
                  <CelebrityCard celeb={a} selected={ans.selectedCelebrityId === a.id} onClick={() => { playSelect(); setAns({ selectedCelebrityId: a.id }); }} testId={`iv-choose-${a.id}`} />
                  <CelebrityCard celeb={b} selected={ans.selectedCelebrityId === b.id} onClick={() => { playSelect(); setAns({ selectedCelebrityId: b.id }); }} testId={`iv-choose-${b.id}`} />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    <VersusBadge small />
                  </div>
                </div>
              </div>
            )}
            {stageIdx === 1 && (
              <div className="space-y-6">
                <ReasonPicker selected={ans.reasons} onChange={(r) => { playSelect(); setAns({ reasons: r }); }} />
                <DecidingFactorPicker selected={ans.decidingFactor} onChange={(f) => { playSelect(); setAns({ decidingFactor: f }); }} />
                <DifficultyPicker selected={ans.difficulty} onChange={(d) => { playSelect(); setAns({ difficulty: d }); }} />
              </div>
            )}
            {stageIdx === 2 && (
              <div>
                <h2 className="font-display text-3xl tracking-tight leading-tight">
                  Who do you think {game.organizerName} predicted for you?
                </h2>
                <div className="relative mt-6 grid grid-cols-2 gap-3">
                  <CelebrityCard celeb={a} selected={ans.majorityPredictionId === a.id} onClick={() => { playSelect(); setAns({ majorityPredictionId: a.id }); }} testId={`iv-predict-${a.id}`} />
                  <CelebrityCard celeb={b} selected={ans.majorityPredictionId === b.id} onClick={() => { playSelect(); setAns({ majorityPredictionId: b.id }); }} testId={`iv-predict-${b.id}`} />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    <VersusBadge small />
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        )}

        <div className="mt-8 flex items-center gap-3">
          <button data-testid="iv-back-btn" onClick={handleBack} className="flex-1 rounded-full border-2 border-white/15 py-3">
            Back
          </button>
          <button
            data-testid="iv-continue-btn"
            onClick={handleContinue}
            disabled={!canContinue}
            className={`flex-[1.4] rounded-full py-3 font-bold flex items-center justify-center gap-2 transition-all ${
              canContinue ? "bg-[#FF107A] text-[#FDFBF7] glow-pink" : "bg-white/10 text-[#FDFBF7]/40 cursor-not-allowed"
            }`}
          >
            {roundIdx === game.rounds.length - 1 && stageIdx === 2 ? "Reveal Comparison" : "Continue"}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
